import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import {
  Paginated,
  PaginationQuery,
  RawPaginationMeta,
  mapPaginationMeta,
} from '../../../../core/models/pagination.model';

export interface MenuItem {
  id: string;
  menu_category_id?: string | null;
  category_name?: string | null;
  tax_id?: string | null;
  tax_name?: string | null;
  name: string;
  slug: string;
  description?: string;
  price: string | number;
  cost: string | number;
  preparation_time: number;
  image?: string | null;
  is_active: boolean;
  modifier_group_ids?: string[];
  created_at?: string;
  updated_at?: string;
}

export interface MenuItemInput {
  name: string;
  menu_category_id?: string | null;
  tax_id?: string | null;
  description?: string;
  price: number;
  cost?: number;
  preparation_time?: number;
  is_active?: boolean;
  image?: File | null;
  modifier_group_ids?: string[];
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: Record<string, string[]>;
}

@Injectable({ providedIn: 'root' })
export class MenuItemsService {
  private readonly apiUrl = `${environment.apiUrl}/menu-items`;

  constructor(private http: HttpClient) {}

  /** Full (unpaginated) list — used by dropdown/cross-feature consumers. */
  getAll(): Observable<ApiResponse<MenuItem[]>> {
    return this.http.get<ApiResponse<MenuItem[]>>(this.apiUrl, {
      params: new HttpParams().set('all', 'true'),
    });
  }

  /** Paginated list for the menu items list page. */
  getPage(query: PaginationQuery = {}): Observable<Paginated<MenuItem>> {
    const params = new HttpParams()
      .set('page', String(query.page ?? 1))
      .set('perPage', String(query.perPage ?? 25));

    return this.http
      .get<ApiResponse<MenuItem[]> & { meta?: RawPaginationMeta }>(this.apiUrl, {
        params,
      })
      .pipe(
        map((res) => ({
          items: res.data ?? [],
          meta: mapPaginationMeta(res.meta, res.data?.length ?? 0),
        })),
      );
  }

  get(id: string): Observable<ApiResponse<MenuItem>> {
    return this.http.get<ApiResponse<MenuItem>>(`${this.apiUrl}/${id}`);
  }

  create(data: MenuItemInput): Observable<ApiResponse<MenuItem>> {
    return this.http.post<ApiResponse<MenuItem>>(this.apiUrl, this.toFormData(data));
  }

  // POST {id} multipart alias so image uploads are parsed by Laravel.
  update(id: string, data: MenuItemInput): Observable<ApiResponse<MenuItem>> {
    return this.http.post<ApiResponse<MenuItem>>(`${this.apiUrl}/${id}`, this.toFormData(data));
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }

  toggleActive(id: string): Observable<ApiResponse<{ id: string; is_active: boolean }>> {
    return this.http.patch<ApiResponse<{ id: string; is_active: boolean }>>(
      `${this.apiUrl}/${id}/toggle-active`,
      {},
    );
  }

  private toFormData(data: MenuItemInput): FormData {
    const fd = new FormData();
    fd.append('name', data.name);
    if (data.menu_category_id) fd.append('menu_category_id', data.menu_category_id);
    if (data.tax_id) fd.append('tax_id', data.tax_id);
    if (data.description != null) fd.append('description', data.description);
    fd.append('price', String(data.price ?? 0));
    fd.append('cost', String(data.cost ?? 0));
    fd.append('preparation_time', String(data.preparation_time ?? 0));
    fd.append('is_active', data.is_active ? '1' : '0');
    if (data.image) fd.append('image', data.image);
    (data.modifier_group_ids ?? []).forEach((gid) => fd.append('modifier_group_ids[]', gid));
    return fd;
  }
}
